import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { DEFAULT_CURRENCY_CODE } from '@constants/common';
import { ErrorCode } from '@constants/errorCodes';
import {
  AccountType,
  OrgStatus,
  SettlementMode,
  SubscriptionPlanExpiredMessage,
  SubscriptionStatus
} from '@constants/generic';
import UserPermissionTypes from '@constants/permissions';
import {
  BRANCHES,
  EVENTS,
  GROUPS,
  MEMBERS,
  SUBSCRIPTIONS
} from '@constants/routes';
import { STORAGE_KEYS } from '@constants/storage';
import {
  useCreateBranchMutation,
  useLazyGetOrgAccountDetailsQuery,
  useOnboardBranchMutation
} from '@features/branch-management/queries';
import useNotify from '@hooks/notify';
import { updateBranchFilter } from '@layouts/main-header/branchSlice';
import { ApiError, BranchForm, ImageForm, ValidationError } from '@types';
import { getNameErrorMessage, isMinLengthSatisfied } from '@utils/generic';
import { getLocalStorageItem, setLocalStorageItem } from '@utils/storage';
import { capitalizeFirstLetter } from '@utils/stringFormat';

const useBranchSelection = ({
  subscriptionData
}: {
  subscriptionData: any;
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const { currentOrg, currentSubscription, rolesAndPermissions } = useSelector(
    (state: any) => state.user
  );
  const { currentBranchFilter } = useSelector((state: any) => state.branch);

  const branchName =
    currentBranchFilter?.branchName ||
    getLocalStorageItem(STORAGE_KEYS.BRANCH_NAME);

  const [createBranchModalOpen, setCreateBranchModalOpen] =
    useState<boolean>(false);
  const [selectedBranch, setSelectedBranch] = useState<string>(branchName);
  const [formData, setFormData] = useState<BranchForm>({
    branchName: '',
    settlementMode: SettlementMode.BANK,
    currency: DEFAULT_CURRENCY_CODE
  });
  const [error, setError] = useState<ValidationError>({});

  const { createErrorAlert, createSuccessAlert } = useNotify();

  const [createBranch, { data: branchCreationData }] =
    useCreateBranchMutation();
  const [getAccountDetails, { data: accountDetails }] =
    useLazyGetOrgAccountDetailsQuery();
  const [onboardBranch] = useOnboardBranchMutation();

  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);

  const showBranchView =
    (currentOrg?.status === OrgStatus.APPROVED ||
      (currentOrg?.status === OrgStatus.KYC_PENDING &&
        !currentOrg?.is_kyc_deferred)) &&
    currentOrg?.has_plan &&
    currentOrg?.type === AccountType.ORGANIZATION;

  const isOrgLite =
    currentOrg?.status === OrgStatus.KYC_PENDING &&
    currentOrg?.type === AccountType.ORGANIZATION &&
    !currentOrg?.is_kyc_deferred;

  const handleGetAccountDetails = async () => {
    const res = await getAccountDetails({
      organizationId,
      queryString: ''
    });
    if ('error' in res) {
      const apiError = res.error as ApiError;
      createErrorAlert(
        apiError.data?.message || 'Error in fetching account details'
      );
    }
  };

  const handleCreateBranch = async () => {
    if (currentSubscription?.status === SubscriptionStatus.EXPIRED) {
      createErrorAlert(SubscriptionPlanExpiredMessage);
      if (
        rolesAndPermissions.permissions.includes(
          UserPermissionTypes.SUBSCRIPTION_VIEW
        )
      ) {
        navigate(SUBSCRIPTIONS.PLAN_DETAILS);
      }
    } else {
      const res = await createBranch({ organizationId });
      if ('data' in res) {
        if (!isOrgLite) handleGetAccountDetails();
      } else if ('error' in res) {
        const apiError = res.error as ApiError;
        createErrorAlert(apiError.data?.message);
        if (apiError?.data?.errorCode === ErrorCode.ERR_SUBSCRIPTION_EXPIRED) {
          if (
            rolesAndPermissions.permissions.includes(
              UserPermissionTypes.SUBSCRIPTION_VIEW
            )
          ) {
            navigate(SUBSCRIPTIONS.PLAN_DETAILS);
          } else {
            createErrorAlert(SubscriptionPlanExpiredMessage);
          }
        }
      }
    }
  };

  const handleAddNewBranch = async () => {
    await handleCreateBranch();
    setCreateBranchModalOpen(true);
  };

  const handleBranchChange = (
    selectedBranchId: string,
    selectedBranchName: string
  ) => {
    if (selectedBranchId) {
      setLocalStorageItem(STORAGE_KEYS.BRANCH_ID, selectedBranchId);
      setLocalStorageItem(STORAGE_KEYS.BRANCH_NAME, selectedBranchName);
      dispatch(
        updateBranchFilter({
          branchId: selectedBranchId,
          branchName: selectedBranchName
        })
      );

      if (/^\/groups\/[^/]+/.test(location.pathname)) {
        navigate(GROUPS.HOME);
      } else if (/^\/events\/[^/]+/.test(location.pathname)) {
        navigate(EVENTS.HOME);
      } else if (/^\/members\/[^/]+/.test(location.pathname)) {
        navigate(MEMBERS.MEMBERS);
      }
    }
  };

  const onChangeForm = (field: string, value: string | ImageForm) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const onChangeBranchName = (value: string) => {
    setError({ name: '' });
    onChangeForm('branchName', value);
  };

  const onChangeCurrency = (value: string) => {
    setError({ currency: '' });
    onChangeForm('currency', value);
  };

  const onBlurBranchName = (value: string) => {
    if (!isMinLengthSatisfied(value)) setError({ name: getNameErrorMessage() });
  };

  const handleSaveNewBranch = async () => {
    const onboardBranchPayload = {
      params: {
        organizationId,
        branchId: branchCreationData?.id
      },
      body: {
        name: formData.branchName.trim(),
        payout_account_type: formData.settlementMode,
        primary_account: accountDetails?.organisation.id,
        is_lite: !!(
          currentOrg?.status === OrgStatus.KYC_PENDING &&
          !currentOrg?.is_kyc_deferred
        ),
        currency_code: formData.currency
      }
    };

    const res = await onboardBranch(onboardBranchPayload);

    // const analyticData = {
    //   org_id: organizationId,
    //   branch_name: onboardBranchPayload.body.name.trim(),
    //   settlement_mode: onboardBranchPayload.body.payout_account_type
    // };

    if ('data' in res) {
      setLocalStorageItem(STORAGE_KEYS.BRANCH_ID, res.data?.id);
      setLocalStorageItem(STORAGE_KEYS.BRANCH_NAME, res.data?.name);
      dispatch(
        updateBranchFilter({
          branchId: res.data?.id,
          branchName: res.data?.name
        })
      );
      setSelectedBranch(res.data?.name);
      createSuccessAlert('Branch created successfully');
      setFormData({
        branchName: '',
        settlementMode: SettlementMode.BANK,
        currency: DEFAULT_CURRENCY_CODE
      });
      setCreateBranchModalOpen(false);
      navigate(BRANCHES.EDIT_BRANCH);
    } else if ('error' in res) {
      const apiError = res.error as ApiError;
      createErrorAlert(
        capitalizeFirstLetter(
          apiError?.data?.message || 'Error in branch creation'
        )
      );
      if (apiError?.data?.errorCode === ErrorCode.ERR_SUBSCRIPTION_EXPIRED) {
        if (
          rolesAndPermissions.permissions.includes(
            UserPermissionTypes.SUBSCRIPTION_VIEW
          )
        ) {
          navigate(SUBSCRIPTIONS.PLAN_DETAILS);
        } else navigate(BRANCHES.EDIT_BRANCH);
      }
    }
  };

  const onClickSave = async () => {
    if (subscriptionData?.status === SubscriptionStatus.EXPIRED) {
      createErrorAlert(SubscriptionPlanExpiredMessage);
      if (
        rolesAndPermissions.permissions.includes(
          UserPermissionTypes.SUBSCRIPTION_VIEW
        )
      ) {
        navigate(SUBSCRIPTIONS.PLAN_DETAILS);
      }
    } else {
      handleSaveNewBranch();
    }
  };

  return {
    showBranchView,
    createBranchModalOpen,
    setCreateBranchModalOpen,
    handleAddNewBranch,
    selectedBranch,
    handleBranchChange,
    formData,
    setFormData,
    onChangeBranchName,
    onChangeCurrency,
    onBlurBranchName,
    onClickSave,
    error
  };
};

export default useBranchSelection;
